// ============================================================================
// src/pages/ForgotPasswordPage.tsx
// ============================================================================
import React, { useState } from "react";
import { useForgotPasswordMutation } from "../services/auth";
import { Link } from "react-router-dom";

export default function ForgotPasswordPage() {
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const [email, setEmail] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    setError(null);

    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      const response = await forgotPassword({ email }).unwrap();

      setFeedback(
        response.message ||
          "If an account exists with that email, a password reset link has been sent. Please check your email."
      );
    } catch (err: any) {
      setError(
        err?.data?.error ||
          err?.data?.message ||
          "Failed to send reset email. Please try again."
      );
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white shadow-lg rounded-lg p-8"
      >
        <h2 className="text-2xl font-bold mb-2 text-gray-900">
          Reset your password
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Enter your email address and we'll send you a link to reset your
          password.
        </p>

        {feedback && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">{feedback}</p>
            <p className="text-xs text-green-600 mt-2">
              Please check your inbox (and spam folder) for the reset link.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="you@example.com"
          />
        </div>

        <button
          type="submit"
          className="w-full px-4 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          disabled={isLoading}
        >
          {isLoading ? "Sending..." : "Send reset link"}
        </button>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
          >
            ← Back to login
          </Link>
        </div>
      </form>
    </div>
  );
}
